import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Alert, CircularProgress, Stack } from "@mui/material";
import DashboardLayout from "../layouts/DashboardLayout";
import ENV from "../config/env";
import menuService from "../modules/menu/menuService";

// 👇 nuevos imports
import { useModemViewer } from "../context/ModemViewerContext";
import { useVehiculoViewer } from "../context/VehiculoViewerContext";

// (Opcional) claves extras que no vienen de /embed/...
const EXTRA_KEYS = {
  docs: true,
  flespi_local: true,
};

export default function IframePage() {
  const { key } = useParams();
  const [keys, setKeys] = useState(null);
  const [error, setError] = useState(null);
  const [phpSynced, setPhpSynced] = useState(false);
  const iframeRef = useRef(null);

  // 👇 obtenemos los viewers (ya están disponibles porque AppRoutes envuelve con los providers)
  const { openModemViewer } = useModemViewer();
  const { openVehiculoViewer } = useVehiculoViewer();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setError(null);
        const backendKeys = await menuService.getEmbedKeys();
        if (!alive) return;
        setKeys({ ...EXTRA_KEYS, ...backendKeys });
      } catch (e) {
        if (!alive) return;
        const code = e?.response?.status || "NETWORK";
        const message = e?.response?.data?.message || e.message || "Error";
        setError({ code, message });
        setKeys({});
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const allowed = useMemo(() => Boolean(keys?.[key]), [keys, key]);

  useEffect(() => {
    function handleMessage(event) {
      // Si quieres, puedes filtrar el origin:
      // if (event.origin !== ENV.PHP_SORT_SERVIDOR) return;

      const data = event.data;

      // ----- ABRIR VIEWER POR MÓDEM -----
      if (data?.type === "OPEN_MODEM" && data.payload) {
        console.log("React abrirá ModemViewer con:", data.payload);
        openModemViewer(data.payload); // por ejemplo: "1122446378"
      }

      // ----- ABRIR VIEWER POR PLACA -----
      if (data?.type === "OPEN_VEHICLE" && data.payload) {
        console.log("React abrirá VehiculoViewer con placa:", data.payload);
        openVehiculoViewer(data.payload); // por ejemplo: "2352-RUX"
      }

      // Ejemplo de alerta simple
      if (data?.type === "SHOW_ALERT") {
        alert(data.message || "Mensaje desde PHP");
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [openModemViewer, openVehiculoViewer]); // 👈 dependencias

  // 👉 cuando ya sabemos que el key es válido, sincronizamos el refresh con PHP
  useEffect(() => {
    if (!allowed) return;
    // leer del mismo storage que usa tu axiosConfig
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      // no hay refresh en el front, igual dejamos pasar
      setPhpSynced(true);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        await fetch(`${ENV.PHP_SORT_SERVIDOR}/files/sync_refresh.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // muy importante para la sesión PHP
          body: JSON.stringify({ refresh_token: refreshToken }),
          signal: controller.signal,
        });
      } catch (err) {
        // si falla no bloqueamos el iframe, pero ya sabemos que PHP puede no tener sesión
      } finally {
        setPhpSynced(true);
      }
    })();

    return () => controller.abort();
  }, [allowed]);

  if (keys === null) {
    return (
      <DashboardLayout>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: "70vh" }}
        >
          <CircularProgress />
          <Alert severity="info" sx={{ mt: 2 }}>
            Cargando permisos de páginas embebidas…
          </Alert>
        </Stack>
      </DashboardLayout>
    );
  }

  if (error?.code === 403) {
    return (
      <DashboardLayout>
        <Alert severity="warning">
          No tienes permisos para consultar <b>/api/menu/embed-keys</b> (403).
        </Alert>
      </DashboardLayout>
    );
  }
  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error">
          No se pudieron cargar los keys del menú. Detalle:{" "}
          {String(error.message || error.code)}
        </Alert>
      </DashboardLayout>
    );
  }

  if (!allowed) {
    return (
      <DashboardLayout>
        <Alert severity="warning">
          No existe una página embebida para: <b>{key}</b>.
        </Alert>
      </DashboardLayout>
    );
  }

  const src =
    ENV.PHP_SORT_SERVIDOR + `/files/launch.php?key=${encodeURIComponent(key)}`;

  return (
    <DashboardLayout>
      <Box sx={{ position: "relative", height: "calc(98vh - 64px)" }}>
        {/* mientras sincronizamos con PHP mostramos un loader chiquito */}
        {!phpSynced ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%" }}
          >
            <CircularProgress size={32} />
            <Alert severity="info" sx={{ mt: 1 }}>
              Preparando sesión segura…
            </Alert>
          </Stack>
        ) : (
          <iframe
            ref={iframeRef}
            title={`embed-${key}`}
            src={src}
            style={{ border: 0, width: "100%", height: "100%" }}
            //sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-modals"
            referrerPolicy="no-referrer-when-downgrade"
          />
        )}
      </Box>
    </DashboardLayout>
  );
}
